import React, { useState, useEffect, useCallback, useMemo } from "react";

// ====== CONSTANTS ======
const DAILY_GOAL_DEFAULT = 20;
const XP_PER_LEARN = 10;
const XP_PER_QUIZ = 20;
const XP_PER_REVIEW = 5;
const WORDS_PER_SESSION = 10;

const LEVELS = [
  { name: "Novice", minXP: 0, icon: "🌱", color: "#56AB91" },
  { name: "Learner", minXP: 500, icon: "📗", color: "#4ECDC4" },
  { name: "Scholar", minXP: 2000, icon: "📘", color: "#63E1FF" },
  { name: "Expert", minXP: 5000, icon: "🎓", color: "#B19CD9" },
  { name: "Master", minXP: 10000, icon: "⚡", color: "#FFD93D" },
  { name: "Grandmaster", minXP: 25000, icon: "👑", color: "#FF6B6B" },
  { name: "Legend", minXP: 50000, icon: "🏆", color: "#FF6B9D" },
  { name: "Conqueror", minXP: 100000, icon: "💎", color: "#8E9DFF" },
];

const BADGES = [
  { id: "first10", name: "First Steps", icon: "👣", desc: "Learn 10 words", check: (s) => s.totalLearned >= 10 },
  { id: "first100", name: "Century", icon: "💯", desc: "Learn 100 words", check: (s) => s.totalLearned >= 100 },
  { id: "first500", name: "Half K", icon: "🔥", desc: "Learn 500 words", check: (s) => s.totalLearned >= 500 },
  { id: "first1000", name: "Thousand", icon: "🏅", desc: "Learn 1000 words", check: (s) => s.totalLearned >= 1000 },
  { id: "first5000", name: "Halfway", icon: "⭐", desc: "Learn 5000 words", check: (s) => s.totalLearned >= 5000 },
  { id: "all10000", name: "Complete!", icon: "🏆", desc: "Learn all 10000 words", check: (s) => s.totalLearned >= 10000 },
  { id: "streak3", name: "On Fire", icon: "🔥", desc: "3-day streak", check: (s) => s.streak >= 3 },
  { id: "streak7", name: "Week Warrior", icon: "⚔️", desc: "7-day streak", check: (s) => s.streak >= 7 },
  { id: "streak30", name: "Month Master", icon: "🗓️", desc: "30-day streak", check: (s) => s.streak >= 30 },
  { id: "quiz50", name: "Quiz Whiz", icon: "🧠", desc: "50 quiz questions", check: (s) => s.totalQuizAnswered >= 50 },
  { id: "quiz90", name: "Sharp Mind", icon: "🎯", desc: "90%+ quiz accuracy", check: (s) => s.totalQuizAnswered >= 20 && (s.totalQuizCorrect / s.totalQuizAnswered) >= 0.9 },
  { id: "review100", name: "Reviewer", icon: "🔄", desc: "Review 100 words", check: (s) => s.totalReviewed >= 100 },
];

const MOTIVATIONAL = [
  "Every word you learn is a weapon for your UPSC arsenal! ⚔️",
  "Vocabulary is the foundation of comprehension. Build it strong! 🏗️",
  "Small daily improvements lead to stunning results. 📈",
  "The difference between ordinary and extraordinary is practice. 💪",
  "Your future self will thank you for every word you learn today. 🌟",
  "Champions are made in the hours nobody watches. 🏆",
  "Consistency beats intensity. Show up every day. 🔥",
  "10,000 words. One word at a time. You've got this! 💎",
  "The pain of discipline weighs ounces, regret weighs tons. ⚡",
  "An IAS officer's vocabulary is their superpower. Build yours! 🦸",
];

// ====== STORAGE HELPERS ======
const STORAGE_KEY = "vocabMasterProgress";

function loadProgress() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function saveProgress(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) { console.error("Failed to save vocab progress:", e); }
}

function getDefaultProgress() {
  return {
    learned: [],       // indices of learned words
    mastered: [],      // indices of mastered words (got right in quiz)
    toReview: [],      // indices needing review
    xp: 0,
    streak: 0,
    lastActiveDate: "",
    dailyGoal: DAILY_GOAL_DEFAULT,
    todayLearned: 0,
    todayDate: "",
    totalQuizAnswered: 0,
    totalQuizCorrect: 0,
    totalReviewed: 0,
    sessions: [],      // {date, wordsLearned, quizCorrect, quizTotal, mode}
    bookmarks: [],     // bookmarked word indices
  };
}

function getTodayStr() { return new Date().toISOString().slice(0, 10); }

// ====== PARSE VOCAB ======
function parseVocab(text) {
  const lines = text.split("\n");
  const words = [];
  for (const line of lines) {
    const match = line.match(/^(\d+)\.\s+\*\*(.+?)\*\*\s*[—–-]\s*(.+)$/);
    if (match) {
      words.push({ num: parseInt(match[1]), word: match[2].trim(), def: match[3].trim() });
    }
  }
  return words;
}

// ====== HELPERS ======
function getLevel(xp) {
  let lvl = LEVELS[0];
  for (const l of LEVELS) { if (xp >= l.minXP) lvl = l; }
  return lvl;
}

function getNextLevel(xp) {
  for (const l of LEVELS) { if (xp < l.minXP) return l; }
  return null;
}

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function getMotivational() { return MOTIVATIONAL[Math.floor(Math.random() * MOTIVATIONAL.length)]; }

// ====== MAIN COMPONENT ======
export default function VocabMaster() {
  const [allWords, setAllWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(() => loadProgress() || getDefaultProgress());
  const [subPage, setSubPage] = useState("home"); // home, learn, quiz, flashcard, review, search, stats, bookmarks
  const [motivation] = useState(getMotivational);

  // Load vocab file
  useEffect(() => {
    fetch("/UPSC_10000_Vocab.md")
      .then(r => r.text())
      .then(text => {
        const w = parseVocab(text);
        setAllWords(w);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Update streak on load
  useEffect(() => {
    const today = getTodayStr();
    setProgress(prev => {
      const p = { ...prev };
      if (p.todayDate !== today) {
        // Check if yesterday was active
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yStr = yesterday.toISOString().slice(0, 10);
        if (p.lastActiveDate === yStr) {
          // streak continues
        } else if (p.lastActiveDate !== today) {
          p.streak = 0;
        }
        p.todayLearned = 0;
        p.todayDate = today;
      }
      return p;
    });
  }, []);

  // Save progress on change
  useEffect(() => { saveProgress(progress); }, [progress]);

  const learnedSet = useMemo(() => new Set(progress.learned), [progress.learned]);
  const masteredSet = useMemo(() => new Set(progress.mastered), [progress.mastered]);
  const bookmarkSet = useMemo(() => new Set(progress.bookmarks), [progress.bookmarks]);

  const updateProgress = useCallback((updater) => {
    setProgress(prev => {
      const next = typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      return next;
    });
  }, []);

  const earnXP = useCallback((amount) => {
    updateProgress(p => {
      const today = getTodayStr();
      return {
        ...p,
        xp: p.xp + amount,
        lastActiveDate: today,
        streak: p.lastActiveDate !== today && p.todayLearned === 0 ? p.streak + 1 : p.streak,
      };
    });
  }, [updateProgress]);

  const level = getLevel(progress.xp);
  const nextLevel = getNextLevel(progress.xp);
  const xpProgress = nextLevel ? ((progress.xp - level.minXP) / (nextLevel.minXP - level.minXP)) * 100 : 100;
  const earnedBadges = BADGES.filter(b => b.check({ totalLearned: progress.learned.length, streak: progress.streak, totalQuizAnswered: progress.totalQuizAnswered, totalQuizCorrect: progress.totalQuizCorrect, totalReviewed: progress.totalReviewed }));
  const dailyProgress = Math.min(100, (progress.todayLearned / progress.dailyGoal) * 100);

  if (loading) {
    return (
      <div className="page" style={{ textAlign: "center", paddingTop: "80px" }}>
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>📚</div>
        <div style={{ fontSize: "16px", fontWeight: 700 }}>Loading 10,000 Words...</div>
        <div style={{ color: "var(--muted)", marginTop: "8px" }}>Preparing your vocabulary arsenal</div>
      </div>
    );
  }

  return (
    <div className="page fade-in">
      {/* Sub-navigation */}
      <div style={{ display: "flex", gap: "6px", marginBottom: "20px", overflowX: "auto", paddingBottom: "4px" }}>
        {[
          { id: "home", icon: "🏠", label: "Home" },
          { id: "learn", icon: "📖", label: "Learn" },
          { id: "quiz", icon: "🧠", label: "Quiz" },
          { id: "flashcard", icon: "🎴", label: "Cards" },
          { id: "review", icon: "🔄", label: "Review" },
          { id: "search", icon: "🔍", label: "Search" },
          { id: "bookmarks", icon: "⭐", label: "Saved" },
          { id: "stats", icon: "📊", label: "Stats" },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setSubPage(tab.id)}
            className={subPage === tab.id ? "btn btn-primary btn-sm" : "btn btn-secondary btn-sm"}
            style={{ whiteSpace: "nowrap", flex: "0 0 auto", width: "auto", minWidth: "70px" }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {subPage === "home" && <VocabHome progress={progress} level={level} nextLevel={nextLevel} xpProgress={xpProgress} earnedBadges={earnedBadges} dailyProgress={dailyProgress} allWords={allWords} motivation={motivation} setSubPage={setSubPage} updateProgress={updateProgress} />}
      {subPage === "learn" && <LearnMode allWords={allWords} progress={progress} updateProgress={updateProgress} learnedSet={learnedSet} earnXP={earnXP} bookmarkSet={bookmarkSet} />}
      {subPage === "quiz" && <QuizMode allWords={allWords} progress={progress} updateProgress={updateProgress} learnedSet={learnedSet} earnXP={earnXP} />}
      {subPage === "flashcard" && <FlashcardMode allWords={allWords} progress={progress} updateProgress={updateProgress} learnedSet={learnedSet} masteredSet={masteredSet} earnXP={earnXP} bookmarkSet={bookmarkSet} />}
      {subPage === "review" && <ReviewMode allWords={allWords} progress={progress} updateProgress={updateProgress} earnXP={earnXP} bookmarkSet={bookmarkSet} />}
      {subPage === "search" && <SearchMode allWords={allWords} learnedSet={learnedSet} masteredSet={masteredSet} bookmarkSet={bookmarkSet} updateProgress={updateProgress} />}
      {subPage === "bookmarks" && <BookmarksMode allWords={allWords} bookmarkSet={bookmarkSet} learnedSet={learnedSet} masteredSet={masteredSet} updateProgress={updateProgress} />}
      {subPage === "stats" && <StatsMode progress={progress} allWords={allWords} earnedBadges={earnedBadges} level={level} updateProgress={updateProgress} />}
    </div>
  );
}

// ====== HOME / DASHBOARD ======
function VocabHome({ progress, level, nextLevel, xpProgress, earnedBadges, dailyProgress, allWords, motivation, setSubPage, updateProgress }) {
  const totalWords = allWords.length;
  const learned = progress.learned.length;
  const pctComplete = totalWords > 0 ? ((learned / totalWords) * 100).toFixed(1) : 0;

  return (
    <>
      <h1 className="page-title">📚 VOCAB MASTERY</h1>
      <p className="page-sub">10,000 words to conquer • Learn daily • Become unstoppable</p>

      {/* Motivation Banner */}
      <div style={{ background: "linear-gradient(135deg, rgba(255,107,107,0.1), rgba(78,205,196,0.1))", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "16px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "14px" }}>
        <div style={{ fontSize: "32px", flexShrink: 0 }}>💡</div>
        <div style={{ fontSize: "13px", color: "var(--muted)", lineHeight: 1.6, fontStyle: "italic" }}>"{motivation}"</div>
      </div>

      {/* Level & XP Card */}
      <div className="card" style={{ marginBottom: "16px", background: "linear-gradient(135deg, rgba(255,107,107,0.05), rgba(177,156,217,0.05))" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "12px" }}>
          <div style={{ fontSize: "40px" }}>{level.icon}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600 }}>Level</div>
            <div style={{ fontSize: "22px", fontWeight: 800, color: level.color }}>{level.name}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "24px", fontWeight: 800, background: "var(--primary-gradient)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>{progress.xp.toLocaleString()}</div>
            <div style={{ fontSize: "10px", color: "var(--muted)" }}>XP</div>
          </div>
        </div>
        {nextLevel && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", color: "var(--muted)", marginBottom: "4px" }}>
              <span>{level.name}</span>
              <span>{nextLevel.name} ({nextLevel.minXP.toLocaleString()} XP)</span>
            </div>
            <div style={{ background: "var(--border)", borderRadius: "6px", height: "8px", overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: "6px", background: "var(--primary-gradient)", width: `${xpProgress}%`, transition: "width 0.5s ease" }} />
            </div>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid-4" style={{ marginBottom: "20px" }}>
        <div className="stat-card">
          <div className="label">🔥 Streak</div>
          <div className="value" style={{ color: progress.streak >= 7 ? "#FF6B6B" : "var(--text)" }}>{progress.streak}</div>
          <div className="sub">days</div>
        </div>
        <div className="stat-card">
          <div className="label">📖 Learned</div>
          <div className="value">{learned}</div>
          <div className="sub">/ {totalWords}</div>
        </div>
        <div className="stat-card">
          <div className="label">📊 Progress</div>
          <div className="value">{pctComplete}%</div>
          <div className="sub">complete</div>
        </div>
        <div className="stat-card">
          <div className="label">🎯 Today</div>
          <div className="value">{progress.todayLearned}</div>
          <div className="sub">/ {progress.dailyGoal} goal</div>
        </div>
      </div>

      {/* Daily Goal Progress */}
      <div className="card" style={{ marginBottom: "16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
          <div style={{ fontSize: "14px", fontWeight: 700 }}>🎯 Daily Goal</div>
          <div style={{ fontSize: "13px", fontWeight: 700, color: dailyProgress >= 100 ? "var(--success)" : "var(--accent)" }}>
            {progress.todayLearned}/{progress.dailyGoal} {dailyProgress >= 100 ? "✅ Complete!" : ""}
          </div>
        </div>
        <div style={{ background: "var(--border)", borderRadius: "8px", height: "12px", overflow: "hidden" }}>
          <div style={{
            height: "100%", borderRadius: "8px", transition: "width 0.5s ease",
            background: dailyProgress >= 100 ? "linear-gradient(90deg, #56AB91, #4ECDC4)" : "var(--primary-gradient)",
            width: `${dailyProgress}%`
          }} />
        </div>
        <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
          <label style={{ fontSize: "11px", color: "var(--muted)" }}>Daily goal: </label>
          <select
            value={progress.dailyGoal}
            onChange={e => updateProgress(p => ({ ...p, dailyGoal: parseInt(e.target.value) }))}
            style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "6px", padding: "2px 8px", fontSize: "12px", color: "var(--text)" }}
          >
            {[10, 15, 20, 25, 30, 40, 50].map(n => <option key={n} value={n}>{n} words</option>)}
          </select>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid-2" style={{ marginBottom: "20px" }}>
        <button className="btn btn-primary" onClick={() => setSubPage("learn")} style={{ padding: "20px", fontSize: "15px" }}>
          📖 Learn New Words
        </button>
        <button className="btn btn-secondary" onClick={() => setSubPage("quiz")} style={{ padding: "20px", fontSize: "15px" }}>
          🧠 Take Quiz
        </button>
        <button className="btn btn-secondary" onClick={() => setSubPage("flashcard")} style={{ padding: "20px", fontSize: "15px" }}>
          🎴 Flashcards
        </button>
        <button className="btn btn-secondary" onClick={() => setSubPage("review")} style={{ padding: "20px", fontSize: "15px" }}>
          🔄 Review ({progress.toReview.length})
        </button>
      </div>

      {/* Badges Preview */}
      <div className="card" style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "14px", fontWeight: 700, marginBottom: "12px" }}>🏅 Badges ({earnedBadges.length}/{BADGES.length})</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {BADGES.map(b => {
            const earned = earnedBadges.includes(b);
            return (
              <div key={b.id} title={b.desc} style={{
                width: "48px", height: "48px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "22px", background: earned ? "rgba(255,107,107,0.1)" : "var(--surface)",
                border: `1px solid ${earned ? "var(--accent)" : "var(--border)"}`,
                opacity: earned ? 1 : 0.3, transition: "all 0.2s", cursor: "pointer"
              }}>
                {b.icon}
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Sessions */}
      {progress.sessions.length > 0 && (
        <div className="card">
          <div style={{ fontSize: "14px", fontWeight: 700, marginBottom: "12px" }}>📜 Recent Sessions</div>
          {progress.sessions.slice(0, 5).map((s, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < 4 ? "1px solid var(--border)" : "none", fontSize: "12px" }}>
              <div>
                <span style={{ color: "var(--muted)" }}>{s.date}</span>
                <span style={{ marginLeft: "8px", fontWeight: 600 }}>{s.mode}</span>
              </div>
              <div style={{ display: "flex", gap: "12px" }}>
                {s.wordsLearned > 0 && <span style={{ color: "var(--success)" }}>📖 {s.wordsLearned}</span>}
                {s.quizTotal > 0 && <span style={{ color: "var(--accent)" }}>🧠 {s.quizCorrect}/{s.quizTotal}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ====== LEARN MODE ======
function LearnMode({ allWords, progress, updateProgress, learnedSet, earnXP, bookmarkSet }) {
  const [currentBatch, setCurrentBatch] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [showDef, setShowDef] = useState(false);
  const [sessionLearned, setSessionLearned] = useState(0);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    // Pick next unlearned words
    const unlearned = allWords.filter((_, i) => !learnedSet.has(i));
    const batch = unlearned.slice(0, WORDS_PER_SESSION);
    setCurrentBatch(batch);
    setCurrentIdx(0);
    setShowDef(false);
    setSessionLearned(0);
    setFinished(false);
  }, [allWords, learnedSet]);

  const word = currentBatch[currentIdx];

  function markLearned() {
    if (!word) return;
    const wordIdx = allWords.findIndex(w => w.num === word.num);
    if (wordIdx >= 0 && !learnedSet.has(wordIdx)) {
      updateProgress(p => ({
        ...p,
        learned: [...p.learned, wordIdx],
        todayLearned: p.todayLearned + 1,
        lastActiveDate: getTodayStr(),
        streak: p.lastActiveDate !== getTodayStr() && p.todayLearned === 0 ? p.streak + 1 : p.streak,
      }));
      earnXP(XP_PER_LEARN);
      setSessionLearned(s => s + 1);
    }
    nextWord();
  }

  function addToReview() {
    if (!word) return;
    const wordIdx = allWords.findIndex(w => w.num === word.num);
    if (wordIdx >= 0) {
      updateProgress(p => ({
        ...p,
        toReview: [...new Set([...p.toReview, wordIdx])],
        learned: [...new Set([...p.learned, wordIdx])],
        todayLearned: p.todayLearned + 1,
        lastActiveDate: getTodayStr(),
      }));
      earnXP(XP_PER_LEARN);
      setSessionLearned(s => s + 1);
    }
    nextWord();
  }

  function toggleBookmark() {
    if (!word) return;
    const wordIdx = allWords.findIndex(w => w.num === word.num);
    if (wordIdx >= 0) {
      updateProgress(p => ({
        ...p,
        bookmarks: bookmarkSet.has(wordIdx) ? p.bookmarks.filter(b => b !== wordIdx) : [...p.bookmarks, wordIdx],
      }));
    }
  }

  function nextWord() {
    if (currentIdx + 1 >= currentBatch.length) {
      // Session complete
      updateProgress(p => ({
        ...p,
        sessions: [{ date: getTodayStr(), wordsLearned: sessionLearned + 1, quizCorrect: 0, quizTotal: 0, mode: "Learn" }, ...p.sessions].slice(0, 100),
      }));
      setFinished(true);
    } else {
      setCurrentIdx(i => i + 1);
      setShowDef(false);
    }
  }

  if (currentBatch.length === 0) {
    return (
      <>
        <h1 className="page-title">📖 LEARN NEW WORDS</h1>
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>🎉</div>
          <div style={{ fontSize: "18px", fontWeight: 700 }}>Amazing! You've seen all words!</div>
          <div style={{ color: "var(--muted)", marginTop: "8px" }}>Go to Review or Quiz to reinforce your knowledge.</div>
        </div>
      </>
    );
  }

  if (finished) {
    return (
      <>
        <h1 className="page-title">📖 SESSION COMPLETE!</h1>
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "64px", marginBottom: "16px" }}>🎉</div>
          <div style={{ fontSize: "22px", fontWeight: 800, background: "var(--primary-gradient)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>+{sessionLearned} Words Learned!</div>
          <div style={{ fontSize: "16px", color: "var(--success)", marginTop: "8px" }}>+{sessionLearned * XP_PER_LEARN} XP earned</div>
          <div style={{ marginTop: "20px", display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <button className="btn btn-primary" onClick={() => { setFinished(false); setCurrentIdx(0); setShowDef(false); setSessionLearned(0); const unlearned = allWords.filter((_, i) => !new Set([...progress.learned]).has(i)); setCurrentBatch(unlearned.slice(0, WORDS_PER_SESSION)); }}>
              📖 Learn More
            </button>
            <button className="btn btn-secondary" onClick={() => setFinished(false)}>
              Done
            </button>
          </div>
        </div>
      </>
    );
  }

  const wordIdx = allWords.findIndex(w => w.num === word.num);
  const isBookmarked = wordIdx >= 0 && bookmarkSet.has(wordIdx);

  return (
    <>
      <h1 className="page-title">📖 LEARN NEW WORDS</h1>
      <p className="page-sub">Word {currentIdx + 1} of {currentBatch.length} • Session: +{sessionLearned} learned</p>

      {/* Progress bar */}
      <div style={{ background: "var(--border)", borderRadius: "8px", height: "6px", marginBottom: "20px", overflow: "hidden" }}>
        <div style={{ height: "100%", borderRadius: "8px", background: "var(--primary-gradient)", width: `${((currentIdx + 1) / currentBatch.length) * 100}%`, transition: "width 0.3s" }} />
      </div>

      {/* Word Card */}
      <div className="card" style={{ textAlign: "center", padding: "32px 24px", marginBottom: "16px", minHeight: "280px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", background: "linear-gradient(135deg, rgba(255,107,107,0.03), rgba(177,156,217,0.03))" }} onClick={() => setShowDef(true)}>
        <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "2px", marginBottom: "12px" }}>Word #{word.num}</div>
        <div style={{ fontSize: "28px", fontWeight: 800, color: "var(--text)", marginBottom: "16px", lineHeight: 1.3 }}>{word.word}</div>

        {showDef ? (
          <div style={{ fontSize: "15px", color: "var(--muted)", lineHeight: 1.8, maxWidth: "500px", animation: "fadeIn 0.3s ease" }}>
            {word.def}
          </div>
        ) : (
          <div style={{ fontSize: "14px", color: "var(--accent)", cursor: "pointer" }}>
            👆 Tap to reveal definition
          </div>
        )}
      </div>

      {/* Action buttons */}
      {showDef && (
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button className="btn btn-primary" style={{ flex: 1, minWidth: "120px" }} onClick={markLearned}>
            ✅ Got it!
          </button>
          <button className="btn btn-secondary" style={{ flex: 1, minWidth: "120px", borderColor: "#FFD93D", color: "#FFD93D" }} onClick={addToReview}>
            🔄 Review Later
          </button>
          <button className="btn btn-secondary" style={{ width: "auto", minWidth: "50px", flex: "0 0 auto" }} onClick={toggleBookmark}>
            {isBookmarked ? "⭐" : "☆"}
          </button>
        </div>
      )}
    </>
  );
}

// ====== QUIZ MODE ======
function QuizMode({ allWords, progress, updateProgress, learnedSet, earnXP }) {
  const [questions, setQuestions] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);

  const generateQuiz = useCallback(() => {
    if (progress.learned.length < 4) return;
    const learnedWords = progress.learned.map(i => allWords[i]).filter(Boolean);
    const shuffled = shuffleArray(learnedWords).slice(0, WORDS_PER_SESSION);
    const qs = shuffled.map(word => {
      const wrongPool = learnedWords.filter(w => w.num !== word.num);
      const wrongs = shuffleArray(wrongPool).slice(0, 3);
      const options = shuffleArray([
        { text: word.def, correct: true },
        ...wrongs.map(w => ({ text: w.def, correct: false })),
      ]);
      return { word, options };
    });
    setQuestions(qs);
    setQIdx(0);
    setSelected(null);
    setShowResult(false);
    setScore(0);
    setFinished(false);
  }, [allWords, progress.learned]);

  useEffect(() => { generateQuiz(); }, [generateQuiz]);

  if (progress.learned.length < 4) {
    return (
      <>
        <h1 className="page-title">🧠 QUIZ MODE</h1>
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>📖</div>
          <div style={{ fontSize: "16px", fontWeight: 700 }}>Learn at least 4 words first!</div>
          <div style={{ color: "var(--muted)", marginTop: "8px" }}>You need a pool of words to generate quiz questions.</div>
        </div>
      </>
    );
  }

  if (finished) {
    const pct = questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;
    return (
      <>
        <h1 className="page-title">🧠 QUIZ COMPLETE!</h1>
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "64px", marginBottom: "16px" }}>{pct >= 80 ? "🏆" : pct >= 60 ? "👍" : "💪"}</div>
          <div style={{ fontSize: "28px", fontWeight: 800, background: "var(--primary-gradient)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>{score}/{questions.length}</div>
          <div style={{ fontSize: "16px", color: "var(--muted)", marginTop: "4px" }}>{pct}% accuracy</div>
          <div style={{ fontSize: "14px", color: "var(--success)", marginTop: "8px" }}>+{score * XP_PER_QUIZ} XP earned</div>
          <div style={{ marginTop: "20px", display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <button className="btn btn-primary" onClick={generateQuiz}>🧠 New Quiz</button>
            <button className="btn btn-secondary" onClick={() => setFinished(false)}>Done</button>
          </div>
        </div>
      </>
    );
  }

  const q = questions[qIdx];
  if (!q) return null;

  function handleSelect(optIdx) {
    if (showResult) return;
    setSelected(optIdx);
    setShowResult(true);
    const isCorrect = q.options[optIdx].correct;
    if (isCorrect) {
      setScore(s => s + 1);
      earnXP(XP_PER_QUIZ);
      // Mark as mastered
      const wordIdx = allWords.findIndex(w => w.num === q.word.num);
      if (wordIdx >= 0) {
        updateProgress(p => ({
          ...p,
          mastered: [...new Set([...p.mastered, wordIdx])],
          toReview: p.toReview.filter(r => r !== wordIdx),
          totalQuizCorrect: p.totalQuizCorrect + 1,
          totalQuizAnswered: p.totalQuizAnswered + 1,
        }));
      }
    } else {
      // Add to review
      const wordIdx = allWords.findIndex(w => w.num === q.word.num);
      if (wordIdx >= 0) {
        updateProgress(p => ({
          ...p,
          toReview: [...new Set([...p.toReview, wordIdx])],
          mastered: p.mastered.filter(m => m !== wordIdx),
          totalQuizAnswered: p.totalQuizAnswered + 1,
        }));
      }
    }
  }

  function handleNext() {
    if (qIdx + 1 >= questions.length) {
      updateProgress(p => ({
        ...p,
        sessions: [{ date: getTodayStr(), wordsLearned: 0, quizCorrect: score + (q.options[selected]?.correct ? 0 : 0), quizTotal: questions.length, mode: "Quiz" }, ...p.sessions].slice(0, 100),
      }));
      setFinished(true);
    } else {
      setQIdx(i => i + 1);
      setSelected(null);
      setShowResult(false);
    }
  }

  return (
    <>
      <h1 className="page-title">🧠 QUIZ MODE</h1>
      <p className="page-sub">Question {qIdx + 1} of {questions.length} • Score: {score}</p>

      <div style={{ background: "var(--border)", borderRadius: "8px", height: "6px", marginBottom: "20px", overflow: "hidden" }}>
        <div style={{ height: "100%", borderRadius: "8px", background: "var(--primary-gradient)", width: `${((qIdx + 1) / questions.length) * 100}%`, transition: "width 0.3s" }} />
      </div>

      <div className="card" style={{ textAlign: "center", padding: "24px", marginBottom: "16px" }}>
        <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "2px", marginBottom: "8px" }}>What does this word mean?</div>
        <div style={{ fontSize: "26px", fontWeight: 800, color: "var(--text)" }}>{q.word.word}</div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "16px" }}>
        {q.options.map((opt, i) => {
          let bg = "var(--card)";
          let border = "var(--border)";
          let color = "var(--text)";
          if (showResult) {
            if (opt.correct) { bg = "rgba(86,171,145,0.15)"; border = "var(--success)"; color = "var(--success)"; }
            else if (i === selected && !opt.correct) { bg = "rgba(255,107,107,0.15)"; border = "var(--accent)"; color = "var(--accent)"; }
          } else if (i === selected) {
            border = "var(--accent)";
          }
          return (
            <div key={i} onClick={() => handleSelect(i)} style={{
              background: bg, border: `2px solid ${border}`, borderRadius: "var(--radius)", padding: "14px 16px",
              cursor: showResult ? "default" : "pointer", transition: "all 0.2s", fontSize: "13px", lineHeight: 1.6, color,
            }}>
              <span style={{ fontWeight: 700, marginRight: "8px" }}>{String.fromCharCode(65 + i)}.</span>
              {opt.text.length > 120 ? opt.text.slice(0, 120) + "..." : opt.text}
            </div>
          );
        })}
      </div>

      {showResult && (
        <button className="btn btn-primary" onClick={handleNext}>
          {qIdx + 1 >= questions.length ? "🏁 See Results" : "➡️ Next Question"}
        </button>
      )}
    </>
  );
}

// ====== FLASHCARD MODE ======
function FlashcardMode({ allWords, progress, updateProgress, learnedSet, masteredSet, earnXP, bookmarkSet }) {
  const [deck, setDeck] = useState([]);
  const [deckIdx, setDeckIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [sessionStats, setSessionStats] = useState({ known: 0, unknown: 0 });
  const [finished, setFinished] = useState(false);
  const [deckSource, setDeckSource] = useState("learned"); // learned, review, all, bookmarks

  const buildDeck = useCallback((source) => {
    let pool = [];
    if (source === "review") {
      pool = progress.toReview.map(i => allWords[i]).filter(Boolean);
    } else if (source === "bookmarks") {
      pool = progress.bookmarks.map(i => allWords[i]).filter(Boolean);
    } else if (source === "all") {
      pool = [...allWords];
    } else {
      pool = progress.learned.map(i => allWords[i]).filter(Boolean);
    }
    const shuffled = shuffleArray(pool).slice(0, 20);
    setDeck(shuffled);
    setDeckIdx(0);
    setFlipped(false);
    setSessionStats({ known: 0, unknown: 0 });
    setFinished(false);
  }, [allWords, progress.learned, progress.toReview, progress.bookmarks]);

  useEffect(() => { buildDeck(deckSource); }, [deckSource, buildDeck]);

  if (deck.length === 0) {
    return (
      <>
        <h1 className="page-title">🎴 FLASHCARDS</h1>
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
          {[{ id: "learned", label: "📖 Learned" }, { id: "review", label: "🔄 Review" }, { id: "bookmarks", label: "⭐ Bookmarks" }, { id: "all", label: "🌐 All" }].map(s => (
            <button key={s.id} className={deckSource === s.id ? "btn btn-primary btn-sm" : "btn btn-secondary btn-sm"} onClick={() => setDeckSource(s.id)} style={{ width: "auto" }}>{s.label}</button>
          ))}
        </div>
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>📭</div>
          <div style={{ fontSize: "16px", fontWeight: 700 }}>No cards in this deck!</div>
          <div style={{ color: "var(--muted)", marginTop: "8px" }}>Learn some words first or try a different deck.</div>
        </div>
      </>
    );
  }

  if (finished) {
    return (
      <>
        <h1 className="page-title">🎴 DECK COMPLETE!</h1>
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "64px", marginBottom: "16px" }}>🎉</div>
          <div style={{ fontSize: "22px", fontWeight: 800, marginBottom: "8px" }}>Session Done!</div>
          <div className="grid-2" style={{ maxWidth: "300px", margin: "0 auto 20px" }}>
            <div className="stat-card"><div className="label">✅ Known</div><div className="value" style={{ color: "var(--success)" }}>{sessionStats.known}</div></div>
            <div className="stat-card"><div className="label">🔄 Review</div><div className="value" style={{ color: "var(--accent)" }}>{sessionStats.unknown}</div></div>
          </div>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <button className="btn btn-primary" onClick={() => buildDeck(deckSource)}>🎴 New Deck</button>
            <button className="btn btn-secondary" onClick={() => setFinished(false)}>Done</button>
          </div>
        </div>
      </>
    );
  }

  const card = deck[deckIdx];
  const wordIdx = allWords.findIndex(w => w.num === card.num);
  const isBookmarked = wordIdx >= 0 && bookmarkSet.has(wordIdx);

  function handleKnow() {
    if (wordIdx >= 0) {
      updateProgress(p => ({
        ...p,
        mastered: [...new Set([...p.mastered, wordIdx])],
        toReview: p.toReview.filter(r => r !== wordIdx),
        totalReviewed: p.totalReviewed + 1,
      }));
      earnXP(XP_PER_REVIEW);
    }
    setSessionStats(s => ({ ...s, known: s.known + 1 }));
    goNext();
  }

  function handleDontKnow() {
    if (wordIdx >= 0) {
      updateProgress(p => ({
        ...p,
        toReview: [...new Set([...p.toReview, wordIdx])],
        mastered: p.mastered.filter(m => m !== wordIdx),
        totalReviewed: p.totalReviewed + 1,
      }));
    }
    setSessionStats(s => ({ ...s, unknown: s.unknown + 1 }));
    goNext();
  }

  function goNext() {
    if (deckIdx + 1 >= deck.length) {
      updateProgress(p => ({
        ...p,
        sessions: [{ date: getTodayStr(), wordsLearned: 0, quizCorrect: sessionStats.known + 1, quizTotal: deck.length, mode: "Flashcard" }, ...p.sessions].slice(0, 100),
      }));
      setFinished(true);
    } else {
      setDeckIdx(i => i + 1);
      setFlipped(false);
    }
  }

  function toggleBookmark() {
    if (wordIdx >= 0) {
      updateProgress(p => ({
        ...p,
        bookmarks: bookmarkSet.has(wordIdx) ? p.bookmarks.filter(b => b !== wordIdx) : [...p.bookmarks, wordIdx],
      }));
    }
  }

  return (
    <>
      <h1 className="page-title">🎴 FLASHCARDS</h1>
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
        {[{ id: "learned", label: "📖 Learned" }, { id: "review", label: "🔄 Review" }, { id: "bookmarks", label: "⭐ Bookmarks" }, { id: "all", label: "🌐 All" }].map(s => (
          <button key={s.id} className={deckSource === s.id ? "btn btn-primary btn-sm" : "btn btn-secondary btn-sm"} onClick={() => setDeckSource(s.id)} style={{ width: "auto" }}>{s.label}</button>
        ))}
      </div>
      <p className="page-sub">Card {deckIdx + 1} of {deck.length} • ✅ {sessionStats.known} • 🔄 {sessionStats.unknown}</p>

      <div style={{ background: "var(--border)", borderRadius: "8px", height: "6px", marginBottom: "20px", overflow: "hidden" }}>
        <div style={{ height: "100%", borderRadius: "8px", background: "var(--primary-gradient)", width: `${((deckIdx + 1) / deck.length) * 100}%`, transition: "width 0.3s" }} />
      </div>

      {/* Flashcard */}
      <div onClick={() => setFlipped(!flipped)} className="vocab-flashcard-flip" style={{
        perspective: "1000px", marginBottom: "16px", cursor: "pointer", minHeight: "260px",
      }}>
        <div style={{
          position: "relative", width: "100%", minHeight: "260px", transition: "transform 0.6s",
          transformStyle: "preserve-3d", transform: flipped ? "rotateY(180deg)" : "rotateY(0)",
        }}>
          {/* Front */}
          <div style={{
            position: "absolute", width: "100%", minHeight: "260px", backfaceVisibility: "hidden",
            background: "linear-gradient(135deg, var(--card), rgba(255,107,107,0.05))", border: "2px solid var(--border)", borderRadius: "var(--radius)",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px",
          }}>
            <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "2px", marginBottom: "16px" }}>Word #{card.num}</div>
            <div style={{ fontSize: "30px", fontWeight: 800, color: "var(--text)", marginBottom: "16px" }}>{card.word}</div>
            <div style={{ fontSize: "13px", color: "var(--accent)" }}>👆 Tap to flip</div>
          </div>
          {/* Back */}
          <div style={{
            position: "absolute", width: "100%", minHeight: "260px", backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            background: "linear-gradient(135deg, var(--card), rgba(86,171,145,0.05))", border: "2px solid var(--success)", borderRadius: "var(--radius)",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px",
          }}>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "var(--text)", marginBottom: "12px" }}>{card.word}</div>
            <div style={{ fontSize: "14px", color: "var(--muted)", lineHeight: 1.8, textAlign: "center", maxWidth: "500px" }}>{card.def}</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: "10px" }}>
        <button className="btn" style={{ flex: 1, background: "rgba(255,107,107,0.15)", color: "var(--accent)", border: "2px solid var(--accent)", minWidth: "100px" }} onClick={handleDontKnow}>
          ❌ Don't Know
        </button>
        <button className="btn" style={{ flex: 1, background: "rgba(86,171,145,0.15)", color: "var(--success)", border: "2px solid var(--success)", minWidth: "100px" }} onClick={handleKnow}>
          ✅ Know It
        </button>
        <button className="btn btn-secondary" style={{ width: "auto", flex: "0 0 auto", minWidth: "50px" }} onClick={toggleBookmark}>
          {isBookmarked ? "⭐" : "☆"}
        </button>
      </div>
    </>
  );
}

// ====== REVIEW MODE ======
function ReviewMode({ allWords, progress, updateProgress, earnXP, bookmarkSet }) {
  const [reviewWords, setReviewWords] = useState([]);
  const [rIdx, setRIdx] = useState(0);
  const [showDef, setShowDef] = useState(false);
  const [finished, setFinished] = useState(false);
  const [sessionCount, setSessionCount] = useState(0);

  useEffect(() => {
    const words = progress.toReview.map(i => allWords[i]).filter(Boolean);
    setReviewWords(shuffleArray(words).slice(0, 15));
    setRIdx(0);
    setShowDef(false);
    setFinished(false);
    setSessionCount(0);
  }, [allWords, progress.toReview]);

  if (reviewWords.length === 0) {
    return (
      <>
        <h1 className="page-title">🔄 REVIEW MODE</h1>
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>✨</div>
          <div style={{ fontSize: "16px", fontWeight: 700 }}>No words to review!</div>
          <div style={{ color: "var(--muted)", marginTop: "8px" }}>Words you mark for review will appear here.</div>
        </div>
      </>
    );
  }

  if (finished) {
    return (
      <>
        <h1 className="page-title">🔄 REVIEW COMPLETE!</h1>
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "64px", marginBottom: "16px" }}>💪</div>
          <div style={{ fontSize: "22px", fontWeight: 800 }}>Reviewed {sessionCount} words!</div>
          <div style={{ fontSize: "14px", color: "var(--success)", marginTop: "8px" }}>+{sessionCount * XP_PER_REVIEW} XP</div>
          <button className="btn btn-primary" style={{ marginTop: "20px" }} onClick={() => { setFinished(false); setRIdx(0); setShowDef(false); }}>
            🔄 Review Again
          </button>
        </div>
      </>
    );
  }

  const word = reviewWords[rIdx];
  const wordIdx = allWords.findIndex(w => w.num === word.num);

  function handleGotIt() {
    updateProgress(p => ({
      ...p,
      mastered: [...new Set([...p.mastered, wordIdx])],
      toReview: p.toReview.filter(r => r !== wordIdx),
      totalReviewed: p.totalReviewed + 1,
    }));
    earnXP(XP_PER_REVIEW);
    setSessionCount(c => c + 1);
    goNext();
  }

  function handleStillLearning() {
    updateProgress(p => ({ ...p, totalReviewed: p.totalReviewed + 1 }));
    setSessionCount(c => c + 1);
    goNext();
  }

  function goNext() {
    if (rIdx + 1 >= reviewWords.length) {
      updateProgress(p => ({
        ...p,
        sessions: [{ date: getTodayStr(), wordsLearned: 0, quizCorrect: sessionCount, quizTotal: reviewWords.length, mode: "Review" }, ...p.sessions].slice(0, 100),
      }));
      setFinished(true);
    } else {
      setRIdx(i => i + 1);
      setShowDef(false);
    }
  }

  function toggleBookmark() {
    if (wordIdx >= 0) {
      updateProgress(p => ({
        ...p,
        bookmarks: bookmarkSet.has(wordIdx) ? p.bookmarks.filter(b => b !== wordIdx) : [...p.bookmarks, wordIdx],
      }));
    }
  }

  return (
    <>
      <h1 className="page-title">🔄 REVIEW MODE</h1>
      <p className="page-sub">Word {rIdx + 1} of {reviewWords.length} • {progress.toReview.length} in review queue</p>

      <div style={{ background: "var(--border)", borderRadius: "8px", height: "6px", marginBottom: "20px", overflow: "hidden" }}>
        <div style={{ height: "100%", borderRadius: "8px", background: "linear-gradient(90deg, #FFD93D, #FF6B6B)", width: `${((rIdx + 1) / reviewWords.length) * 100}%`, transition: "width 0.3s" }} />
      </div>

      <div className="card" style={{ textAlign: "center", padding: "32px 24px", marginBottom: "16px", minHeight: "240px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", borderColor: "#FFD93D", borderWidth: "2px" }} onClick={() => setShowDef(true)}>
        <div style={{ fontSize: "11px", color: "#FFD93D", textTransform: "uppercase", letterSpacing: "2px", marginBottom: "12px" }}>Review — Word #{word.num}</div>
        <div style={{ fontSize: "28px", fontWeight: 800, color: "var(--text)", marginBottom: "16px" }}>{word.word}</div>
        {showDef ? (
          <div style={{ fontSize: "15px", color: "var(--muted)", lineHeight: 1.8, maxWidth: "500px", animation: "fadeIn 0.3s ease" }}>{word.def}</div>
        ) : (
          <div style={{ fontSize: "14px", color: "#FFD93D" }}>👆 Tap to reveal</div>
        )}
      </div>

      {showDef && (
        <div style={{ display: "flex", gap: "10px" }}>
          <button className="btn" style={{ flex: 1, background: "rgba(86,171,145,0.15)", color: "var(--success)", border: "2px solid var(--success)" }} onClick={handleGotIt}>✅ Got it!</button>
          <button className="btn" style={{ flex: 1, background: "rgba(255,217,61,0.15)", color: "#FFD93D", border: "2px solid #FFD93D" }} onClick={handleStillLearning}>🔄 Still Learning</button>
          <button className="btn btn-secondary" style={{ width: "auto", flex: "0 0 auto", minWidth: "50px" }} onClick={toggleBookmark}>
            {bookmarkSet.has(wordIdx) ? "⭐" : "☆"}
          </button>
        </div>
      )}
    </>
  );
}

// ====== SEARCH MODE ======
function SearchMode({ allWords, learnedSet, masteredSet, bookmarkSet, updateProgress }) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const PER_PAGE = 50;

  const filtered = useMemo(() => {
    if (!query.trim()) return allWords;
    const q = query.toLowerCase().trim();
    return allWords.filter(w => w.word.toLowerCase().includes(q) || w.def.toLowerCase().includes(q));
  }, [query, allWords]);

  const pageWords = filtered.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  function toggleBookmark(wordIdx) {
    updateProgress(p => ({
      ...p,
      bookmarks: bookmarkSet.has(wordIdx) ? p.bookmarks.filter(b => b !== wordIdx) : [...p.bookmarks, wordIdx],
    }));
  }

  return (
    <>
      <h1 className="page-title">🔍 SEARCH VOCABULARY</h1>
      <p className="page-sub">{allWords.length.toLocaleString()} words • Search by word or definition</p>

      <input
        className="form-input"
        placeholder="🔍 Type to search words or definitions..."
        value={query}
        onChange={e => { setQuery(e.target.value); setPage(0); }}
        style={{ marginBottom: "16px", fontSize: "15px" }}
      />

      <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "12px" }}>
        Showing {pageWords.length} of {filtered.length} results
        {totalPages > 1 && ` • Page ${page + 1} of ${totalPages}`}
      </div>

      {pageWords.map(w => {
        const idx = allWords.findIndex(aw => aw.num === w.num);
        const isLearned = learnedSet.has(idx);
        const isMastered = masteredSet.has(idx);
        const isBm = bookmarkSet.has(idx);
        return (
          <div key={w.num} style={{
            background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)",
            padding: "12px 16px", marginBottom: "8px", display: "flex", gap: "12px", alignItems: "flex-start",
            borderLeft: `3px solid ${isMastered ? "var(--success)" : isLearned ? "var(--accent)" : "var(--border)"}`,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "10px", color: "var(--muted)" }}>#{w.num}</span>
                <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--text)" }}>{w.word}</span>
                {isMastered && <span style={{ fontSize: "9px", background: "rgba(86,171,145,0.2)", color: "var(--success)", padding: "1px 6px", borderRadius: "4px", fontWeight: 600 }}>MASTERED</span>}
                {isLearned && !isMastered && <span style={{ fontSize: "9px", background: "rgba(255,107,107,0.15)", color: "var(--accent)", padding: "1px 6px", borderRadius: "4px", fontWeight: 600 }}>LEARNED</span>}
              </div>
              <div style={{ fontSize: "12px", color: "var(--muted)", lineHeight: 1.6 }}>{w.def}</div>
            </div>
            <button onClick={() => toggleBookmark(idx)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", flexShrink: 0, padding: "4px" }}>
              {isBm ? "⭐" : "☆"}
            </button>
          </div>
        );
      })}

      {totalPages > 1 && (
        <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginTop: "16px", flexWrap: "wrap" }}>
          <button className="btn btn-secondary btn-sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} style={{ width: "auto", opacity: page === 0 ? 0.4 : 1 }}>← Prev</button>
          <span style={{ display: "flex", alignItems: "center", fontSize: "12px", color: "var(--muted)" }}>Page {page + 1}/{totalPages}</span>
          <button className="btn btn-secondary btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} style={{ width: "auto", opacity: page >= totalPages - 1 ? 0.4 : 1 }}>Next →</button>
        </div>
      )}
    </>
  );
}

// ====== BOOKMARKS MODE ======
function BookmarksMode({ allWords, bookmarkSet, learnedSet, masteredSet, updateProgress }) {
  const bookmarkedWords = useMemo(() => {
    return [...bookmarkSet].map(i => ({ ...allWords[i], idx: i })).filter(w => w.word);
  }, [allWords, bookmarkSet]);

  function removeBookmark(idx) {
    updateProgress(p => ({ ...p, bookmarks: p.bookmarks.filter(b => b !== idx) }));
  }

  return (
    <>
      <h1 className="page-title">⭐ SAVED WORDS</h1>
      <p className="page-sub">{bookmarkedWords.length} bookmarked words</p>

      {bookmarkedWords.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>⭐</div>
          <div style={{ fontSize: "16px", fontWeight: 700 }}>No bookmarks yet!</div>
          <div style={{ color: "var(--muted)", marginTop: "8px" }}>Bookmark words while learning to save them here.</div>
        </div>
      ) : (
        bookmarkedWords.map(w => (
          <div key={w.num} style={{
            background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)",
            padding: "14px 16px", marginBottom: "8px", borderLeft: "3px solid #FFD93D",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                  <span style={{ fontSize: "10px", color: "var(--muted)" }}>#{w.num}</span>
                  <span style={{ fontSize: "14px", fontWeight: 700 }}>{w.word}</span>
                  {masteredSet.has(w.idx) && <span style={{ fontSize: "9px", background: "rgba(86,171,145,0.2)", color: "var(--success)", padding: "1px 6px", borderRadius: "4px", fontWeight: 600 }}>MASTERED</span>}
                </div>
                <div style={{ fontSize: "12px", color: "var(--muted)", lineHeight: 1.6 }}>{w.def}</div>
              </div>
              <button onClick={() => removeBookmark(w.idx)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "16px", color: "var(--accent)", padding: "4px" }}>✕</button>
            </div>
          </div>
        ))
      )}
    </>
  );
}

// ====== STATS MODE ======
function StatsMode({ progress, allWords, earnedBadges, level, updateProgress }) {
  const totalWords = allWords.length;
  const learned = progress.learned.length;
  const mastered = progress.mastered.length;
  const inReview = progress.toReview.length;
  const quizAcc = progress.totalQuizAnswered > 0 ? Math.round((progress.totalQuizCorrect / progress.totalQuizAnswered) * 100) : 0;

  // Group sessions by date
  const sessionsByDate = {};
  progress.sessions.forEach(s => {
    if (!sessionsByDate[s.date]) sessionsByDate[s.date] = [];
    sessionsByDate[s.date].push(s);
  });

  // Last 7 days data
  const last7 = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().slice(0, 10);
    const sessions = sessionsByDate[dateStr] || [];
    const wordsLearned = sessions.reduce((s, sess) => s + (sess.wordsLearned || 0), 0);
    last7.push({ date: dateStr, day: d.toLocaleDateString("en", { weekday: "short" }), wordsLearned });
  }
  const max7 = Math.max(...last7.map(d => d.wordsLearned), 1);

  return (
    <>
      <h1 className="page-title">📊 VOCABULARY STATS</h1>
      <p className="page-sub">Your journey in numbers</p>

      {/* Overview */}
      <div className="grid-4" style={{ marginBottom: "20px" }}>
        <div className="stat-card"><div className="label">📖 Learned</div><div className="value">{learned}</div><div className="sub">/ {totalWords}</div></div>
        <div className="stat-card"><div className="label">✅ Mastered</div><div className="value" style={{ color: "var(--success)" }}>{mastered}</div><div className="sub">words</div></div>
        <div className="stat-card"><div className="label">🔄 In Review</div><div className="value" style={{ color: "#FFD93D" }}>{inReview}</div><div className="sub">words</div></div>
        <div className="stat-card"><div className="label">🧠 Quiz Acc.</div><div className="value" style={{ color: "var(--accent)" }}>{quizAcc}%</div><div className="sub">{progress.totalQuizAnswered} answered</div></div>
      </div>

      {/* Big Progress Ring */}
      <div className="card" style={{ textAlign: "center", padding: "28px", marginBottom: "20px" }}>
        <div style={{ fontSize: "14px", fontWeight: 700, marginBottom: "16px" }}>Overall Progress</div>
        <div className="progress-ring-container">
          <svg className="progress-ring" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="42" stroke="var(--border)" strokeWidth="8" fill="none" />
            <circle cx="50" cy="50" r="42" stroke="url(#vocabGrad)" strokeWidth="8" fill="none"
              strokeLinecap="round" className="progress-ring-circle"
              strokeDasharray={`${2 * Math.PI * 42}`}
              strokeDashoffset={`${2 * Math.PI * 42 * (1 - learned / Math.max(totalWords, 1))}`} />
            <defs><linearGradient id="vocabGrad" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="#FF6B6B" /><stop offset="100%" stopColor="#FFD93D" /></linearGradient></defs>
          </svg>
          <div className="progress-ring-text">
            <span>{totalWords > 0 ? ((learned / totalWords) * 100).toFixed(1) : 0}%</span>
          </div>
        </div>
        <div style={{ fontSize: "12px", color: "var(--muted)", marginTop: "12px" }}>{learned.toLocaleString()} of {totalWords.toLocaleString()} words</div>
      </div>

      {/* 7-day chart */}
      <div className="card" style={{ marginBottom: "20px" }}>
        <div style={{ fontSize: "14px", fontWeight: 700, marginBottom: "16px" }}>📈 Last 7 Days</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", height: "120px" }}>
          {last7.map((d, i) => (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
              <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--text)" }}>{d.wordsLearned}</div>
              <div style={{
                width: "100%", borderRadius: "6px 6px 0 0", minHeight: "4px",
                height: `${(d.wordsLearned / max7) * 80}px`,
                background: d.wordsLearned > 0 ? "var(--primary-gradient)" : "var(--border)",
                transition: "height 0.3s",
              }} />
              <div style={{ fontSize: "9px", color: "var(--muted)" }}>{d.day}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Badges */}
      <div className="card" style={{ marginBottom: "20px" }}>
        <div style={{ fontSize: "14px", fontWeight: 700, marginBottom: "16px" }}>🏅 Badges ({earnedBadges.length}/{BADGES.length})</div>
        <div className="grid-auto" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }}>
          {BADGES.map(b => {
            const earned = earnedBadges.includes(b);
            return (
              <div key={b.id} style={{
                background: earned ? "rgba(255,107,107,0.08)" : "var(--surface)",
                border: `1px solid ${earned ? "var(--accent)" : "var(--border)"}`,
                borderRadius: "var(--radius-sm)", padding: "12px", textAlign: "center",
                opacity: earned ? 1 : 0.4,
              }}>
                <div style={{ fontSize: "28px", marginBottom: "4px" }}>{b.icon}</div>
                <div style={{ fontSize: "11px", fontWeight: 700 }}>{b.name}</div>
                <div style={{ fontSize: "9px", color: "var(--muted)" }}>{b.desc}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Session History */}
      <div className="card" style={{ marginBottom: "20px" }}>
        <div style={{ fontSize: "14px", fontWeight: 700, marginBottom: "12px" }}>📜 Session History</div>
        {progress.sessions.length === 0 ? (
          <div style={{ color: "var(--muted)", fontSize: "13px" }}>No sessions yet. Start learning!</div>
        ) : (
          <div style={{ maxHeight: "300px", overflowY: "auto" }}>
            {progress.sessions.slice(0, 30).map((s, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--border)", fontSize: "12px" }}>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <span style={{ color: "var(--muted)" }}>{s.date}</span>
                  <span style={{
                    padding: "2px 8px", borderRadius: "4px", fontSize: "10px", fontWeight: 600,
                    background: s.mode === "Learn" ? "rgba(86,171,145,0.15)" : s.mode === "Quiz" ? "rgba(255,107,107,0.15)" : s.mode === "Flashcard" ? "rgba(177,156,217,0.15)" : "rgba(255,217,61,0.15)",
                    color: s.mode === "Learn" ? "var(--success)" : s.mode === "Quiz" ? "var(--accent)" : s.mode === "Flashcard" ? "#B19CD9" : "#FFD93D",
                  }}>{s.mode}</span>
                </div>
                <div style={{ display: "flex", gap: "12px" }}>
                  {s.wordsLearned > 0 && <span>📖 {s.wordsLearned}</span>}
                  {s.quizTotal > 0 && <span>🧠 {s.quizCorrect}/{s.quizTotal}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reset Progress */}
      <div className="card" style={{ borderColor: "rgba(255,107,107,0.3)" }}>
        <div style={{ fontSize: "14px", fontWeight: 700, marginBottom: "8px", color: "var(--accent)" }}>⚠️ Danger Zone</div>
        <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "12px" }}>Reset all vocabulary progress. This cannot be undone.</div>
        <button className="btn btn-secondary" style={{ borderColor: "var(--accent)", color: "var(--accent)", width: "auto" }} onClick={() => {
          if (window.confirm("Are you sure? This will reset ALL your vocabulary progress!")) {
            updateProgress(() => getDefaultProgress());
          }
        }}>
          Reset Progress
        </button>
      </div>
    </>
  );
}
